import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Container, Row, Col, Button, Image, Card, Form, Modal } from 'react-bootstrap';
import {
  FaUser, FaBuilding, FaPhoneAlt, FaEye, FaRegHeart, FaShareAlt,
  FaEnvelope, FaIdCard, FaCheckCircle, FaUserCircle, FaLaptop,
  FaBriefcase, FaWhatsapp, FaTelegramPlane, FaMailBulk, FaBold,
  FaHome, FaCalendarAlt, FaExchangeAlt, FaMapMarkerAlt, FaFileAlt,
  FaPassport, FaMoneyBillAlt, FaInfoCircle, FaUtensils, FaStar,
  FaLanguage, FaCogs, FaUserFriends, FaGraduationCap, FaVideo
} from 'react-icons/fa';
import { FaCar, FaUserNurse, FaStethoscope, FaUserTie, FaRunning, FaTools } from 'react-icons/fa';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import BaseUrl from '../../ApiServices/BaseUrl';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import moment from 'moment';
import './AgencyViewCandidates.css';
import defaultProfileImage from '../../Pages/Images/defaultimage.webp';
import { getVisibleOfferBadgeText, getVisibleSourceLabel, hasVisibleOfferBadge } from '../../../utils/candidateBadges';
import { FaBots } from 'react-icons/fa6';

const AgencyViewCandidates = () => {
  const [profileData, setProfileData] = useState(null);
  const [relatedCandidates, setRelatedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasPostedJob, setHasPostedJob] = useState(false);
  const [checkingJobPost, setCheckingJobPost] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);
  const [isDoctorProfile, setIsDoctorProfile] = useState(false);
  const [agencyContactDetails, setAgencyContactDetails] = useState(null);
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [unlockExpiry, setUnlockExpiry] = useState(null);
  const [reportData, setReportData] = useState({
    email: '',
    reason: '',
    description: ''
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { userId, first_name } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user')) || {};

  // Helper function to safely parse array fields
  const safeParseArray = (field) => {
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field.replace(/\\"/g, '"'));
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const getJobPosition = () => {
    if (!profileData?.job_position) return '';

    const positions = safeParseArray(profileData.job_position);

    if (Array.isArray(positions) && positions.length > 0) {
      return positions[0].toLowerCase();
    }
    return '';
  };


  // Check specific job types
  const isCook = () => jobPosition.includes('cook');
  const isTeacher = () => jobPosition.includes('teacher') || jobPosition.includes('tutor');
  const isNurse = () => jobPosition.includes('nurse');
  const isDoctor = () => jobPosition.includes('doctor') || jobPosition.includes('physician');
  const isDriver = () => jobPosition.includes('driver');
  const isTailor = () => jobPosition.includes('tailor');
  const isTrainer = () => jobPosition.includes('trainer') || jobPosition.includes('coach');
  const isBeautician = () => jobPosition.includes('beautician');
  const isDomesticHelper = () => jobPosition.includes('domestic') || jobPosition.includes('helper') || jobPosition.includes('maid');

  const jobPosition = getJobPosition();


  // Specialized fields parsing
  const drivingLicenseCountries = safeParseArray(profileData?.driving_license_countries);
  const carTypesDriven = safeParseArray(profileData?.car_types_driven);
  const previousCarsDriven = safeParseArray(profileData?.previous_cars_driven);
  const nursingQualifications = safeParseArray(profileData?.nursing_qualifications);
  const nursingPositions = safeParseArray(profileData?.nursing_positions);
  const nursingJobCountries = safeParseArray(profileData?.nursing_job_countries);
  const nursingLicenseCountries = safeParseArray(profileData?.nursing_license_countries);
  const teachingSubjects = safeParseArray(profileData?.subjects);
  const teachingAgeGroups = safeParseArray(profileData?.age_groups);
  const teachingClasses = safeParseArray(profileData?.classes);
  const beauticianServices = {
    henna: safeParseArray(profileData?.henna_services),
    makeup: safeParseArray(profileData?.makeup_services),
    skinCare: safeParseArray(profileData?.skin_care_services),
    nail: safeParseArray(profileData?.nail_services),
    hair: safeParseArray(profileData?.hair_services),
    massage: safeParseArray(profileData?.massage_spa_services)
  };
  const tailorSkills = {
    mode: profileData?.tailor_mode,
    type: profileData?.tailor_type,
    stitching: safeParseArray(profileData?.stitching_preferences),
    fabric: safeParseArray(profileData?.fabric_preference),
    style: safeParseArray(profileData?.style_preference)
  };
  const trainingDetails = {
    mode: profileData?.training_mode,
    sports: safeParseArray(profileData?.sports_games),
    ageGroups: safeParseArray(profileData?.sports_age_groups)
  };



  useEffect(() => {
    // Get current user from localStorage
    const user = JSON.parse(localStorage.getItem('user')) || null;
    setCurrentUser(user);
    if (user?.email) {
      setReportData(prev => ({ ...prev, email: user.email }));
    }

    const fetchProfileData = async () => {
      try {
        console.log("Fetching data for userId:", userId);
        const response = await fetch(`${BaseUrl}/api/job-seeker/merged/${userId}`);
        console.log("Raw response:", response);

        if (!response.ok) {
          throw new Error('Failed to fetch profile data');
        }

        const data = await response.json();
        console.log("Fetched profile data:", data);

        // Construct full URL for the profile photo if it exists
        if (data.profile_photo) {
          data.profile_photo = `${BaseUrl}${data.profile_photo}`;
          console.log("Updated profile photo URL:", data.profile_photo);
        }

        const jobPositions = Array.isArray(data.job_position)
          ? data.job_position
          : safeParseArray(data.job_position || "[]");

        // Doctor profiles should stay in the shared nurse/common candidate flow.
        setIsDoctorProfile(false);

        // Parse array fields
        data.job_position = jobPositions;
        data.language = safeParseArray(data.language);
        data.main_skills = safeParseArray(data.main_skills);
        data.cooking_skills = safeParseArray(data.cooking_skills);
        data.other_skills = safeParseArray(data.other_skills);
        data.personality = safeParseArray(data.personality);
        data.experience = safeParseArray(data.experience);
        data.preffered_job_locations = safeParseArray(data.preffered_job_locations);
        data.education = safeParseArray(data.education);

        // Parse doctor-specific fields if they exist
        if (data.dtr_country_doctor_license && typeof data.dtr_country_doctor_license === 'string') {
          try {
            data.dtr_country_doctor_license = JSON.parse(data.dtr_country_doctor_license);
          } catch (e) {
            data.dtr_country_doctor_license = [];
          }
        }

        if (data.dtr_previous_hospital_details && typeof data.dtr_previous_hospital_details === 'string') {
          try {
            data.dtr_previous_hospital_details = JSON.parse(data.dtr_previous_hospital_details);
          } catch (e) {
            data.dtr_previous_hospital_details = [];
          }
        }

        if (data.dtr_preferred_country && typeof data.dtr_preferred_country === 'string') {
          try {
            data.dtr_preferred_country = JSON.parse(data.dtr_preferred_country);
          } catch (e) {
            data.dtr_preferred_country = [];
          }
        }

        if (data.dtr_language_skilled && typeof data.dtr_language_skilled === 'string') {
          try {
            data.dtr_language_skilled = JSON.parse(data.dtr_language_skilled);
          } catch (e) {
            data.dtr_language_skilled = [];
          }
        }

        if (data.dtr_driving_license_country && typeof data.dtr_driving_license_country === 'string') {
          try {
            data.dtr_driving_license_country = JSON.parse(data.dtr_driving_license_country);
          } catch (e) {
            data.dtr_driving_license_country = [];
          }
        }

        if (data.dtr_before_worked_country && typeof data.dtr_before_worked_country === 'string') {
          try {
            data.dtr_before_worked_country = JSON.parse(data.dtr_before_worked_country);
          } catch (e) {
            data.dtr_before_worked_country = [];
          }
        }

        if (data.dtr_preferred_job_location && typeof data.dtr_preferred_job_location === 'string') {
          try {
            data.dtr_preferred_job_location = JSON.parse(data.dtr_preferred_job_location);
          } catch (e) {
            data.dtr_preferred_job_location = [];
          }
        }

        setProfileData(data);

        // Check if user has already reported this candidate
        if (user?.id) {
          const reportResponse = await fetch(`${BaseUrl}/api/candidatereport`);
          const reportData = await reportResponse.json();
          if (reportResponse.ok) {
            const hasReported = reportData.some(
              report => report.emp_id === user.id && report.candidate_id === parseInt(userId)
            );
            setAlreadyReported(hasReported);
          }
        }

        // Fetch related candidates based on job_position
        if (data.job_position && data.job_position.length > 0) {
          const candidatesResponse = await fetch(`${BaseUrl}/api/job-seeker`);
          if (!candidatesResponse.ok) {
            throw new Error('Failed to fetch related candidates');
          }
          let candidatesData = await candidatesResponse.json();

          // Filter candidates based on verification status and plan_enddate
          const currentDate = new Date().toISOString().split('T')[0];
          candidatesData = candidatesData.filter(candidate => {
            if (candidate.verification_status?.toLowerCase() !== 'verified') {
              return false;
            }
            if (candidate.source?.toLowerCase() === 'agency') {
              return candidate.plan_enddate && new Date(candidate.plan_enddate) >= new Date(currentDate);
            }
            return true;
          });

          // Parse array fields for related candidates
          candidatesData = candidatesData.map(candidate => ({
            ...candidate,
            job_position: safeParseArray(candidate.job_position),
            language: safeParseArray(candidate.language),
            main_skills: safeParseArray(candidate.main_skills),
            preferred_countries: safeParseArray(candidate.preferred_countries),
            work_experience: safeParseArray(candidate.work_experience),
            profile_photo: candidate.profile_photo ? `${BaseUrl}${candidate.profile_photo}` : candidate.profile_photo
          }));

          // Filter related candidates by matching job_position, excluding the current candidate
          const related = candidatesData.filter(candidate =>
            candidate.user_id !== parseInt(userId) &&
            candidate.job_position.some(pos =>
              data.job_position.some(p => p.toLowerCase() === pos.toLowerCase())
            )
          );
          setRelatedCandidates(related);
        }
      } catch (err) {
        console.error("Error fetching profile data:", err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfileData();
    }
  }, [userId]);

  // Check if contact is already unlocked
  useEffect(() => {
    const checkContactUnlock = async () => {
      if (!currentUser?.id || !userId) return;
      try {
        const res = await fetch(`${BaseUrl}/api/agency/contact-unlock/check?agency_id=${currentUser.id}&candidate_user_id=${userId}`);
        const data = await res.json();
        if (data.unlocked) {
          setContactUnlocked(true);
          setUnlockExpiry(data.expires_at);
        }
      } catch (err) {
        console.error('Error checking contact unlock:', err);
      }
    };
    checkContactUnlock();
  }, [currentUser, userId]);

  // Fetch agency contact details if candidate source is Agency
  useEffect(() => {
    const fetchAgencyContactDetails = async () => {
      if (profileData?.source?.toLowerCase() === 'agency' && profileData.agency_uid) {
        try {
          const response = await fetch(`${BaseUrl}/api/agency`);
          if (!response.ok) {
            throw new Error('Failed to fetch agency data');
          }
          const agencies = await response.json();
          const candidateAgency = agencies.find(agency => agency.id === profileData.agency_uid);

          if (candidateAgency) {
            setAgencyContactDetails({
              agency_name: candidateAgency.agency_name,
              email: candidateAgency.email,
              whatsapp_number_1: candidateAgency.whatsapp_number_1,
              whatsapp_number_1_country_code: candidateAgency.whatsapp_number_1_country_code,
              whatsapp_number_2: candidateAgency.whatsapp_number_2,
              whatsapp_number_2_country_code: candidateAgency.whatsapp_number_2_country_code,
              whatsapp_number_3: candidateAgency.whatsapp_number_3,
              whatsapp_number_3_country_code: candidateAgency.whatsapp_number_3_country_code,
              whatsapp_number_4: candidateAgency.whatsapp_number_4,
              whatsapp_number_4_country_code: candidateAgency.whatsapp_number_4_country_code,
              official_phone: candidateAgency.official_phone,
              official_phone_country_code: candidateAgency.official_phone_country_code,
              owner_mobile: candidateAgency.owner_mobile,
              owner_mobile_country_code: candidateAgency.owner_mobile_country_code,
              manager_mobile: candidateAgency.manager_mobile,
              manager_mobile_country_code: candidateAgency.manager_mobile_country_code
            });
          }
        } catch (err) {
          console.error("Error fetching agency details:", err);
        }
      }
    };

    if (profileData) {
      fetchAgencyContactDetails();
    }
  }, [profileData]);

  // Check if current user has posted any jobs
  const checkIfUserHasPostedJob = async () => {
    if (!currentUser) return false;
    setCheckingJobPost(true);
    try {
      const response = await fetch(`${BaseUrl}/api/employer/all`);
      if (!response.ok) {
        throw new Error('Failed to fetch employer data');
      }
      const data = await response.json();
      const userHasPostedJob = data.some(job => job.user_id === currentUser.id);
      setHasPostedJob(userHasPostedJob);
      return userHasPostedJob;
    } catch (err) {
      console.error("Error checking job posts:", err);
      return false;
    } finally {
      setCheckingJobPost(false);
    }
  };

  // Handle contact button click (Call / WhatsApp buttons at top)
  const handleContactClick = async () => {
    if (!currentUser) {
      await Swal.fire({
        title: 'Login Required',
        text: 'Please login as an agency to contact candidates',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      navigate('/basicinfo');
      return;
    }

    try {
      // Check if already unlocked
      if (contactUnlocked) {
        // Already unlocked – directly call
        openCandidateCall();
        return;
      }

      // Check subscription info
      const featureCheck = await axios.get(`${BaseUrl}/api/agency/check-feature`, {
        params: { agencyId: currentUser.id, feature: 'contact_unlock', category: 'contact' }
      });
      const featureData = featureCheck.data;

      if (!featureData.allowed) {
        if (featureData.reason === 'no_subscription') {
          Swal.fire({
            icon: 'warning', title: 'No Subscription',
            text: 'You need an active Candidate Contact subscription to contact candidates.',
            confirmButtonText: 'View Plans', showCancelButton: true
          }).then(r => { if (r.isConfirmed) navigate('/a-subscriptionplans'); });
        } else if (featureData.reason === 'limit_reached') {
          Swal.fire({ icon: 'error', title: 'Limit Reached', text: `You have used all ${featureData.limit} contact unlocks for your plan.` });
        } else if (featureData.reason === 'insufficient_wallet') {
          Swal.fire({
            icon: 'error', title: 'Insufficient Balance',
            text: `Your contact wallet balance (${featureData.balance}) is less than the unlock cost (${featureData.required}). Please top up.`,
            confirmButtonText: 'Top Up', showCancelButton: true
          }).then(r => { if (r.isConfirmed) navigate('/a-subscriptionplans'); });
        } else {
          Swal.fire({ icon: 'warning', title: 'Subscription Required', text: 'Please subscribe to a Candidate Contact plan.' });
        }
        return;
      }

      // For PPC, show confirmation
      if (featureData.plan_type === 'pay_per_click') {
        const result = await Swal.fire({
          title: 'Unlock Contact Details?',
          html: `
            <div style="text-align: left; background: #fff; border-radius: 12px; padding: 5px; font-family: 'Inter', sans-serif;">
                <div style="background: #f8f9fa; border-radius: 10px; padding: 15px; border: 1px solid #e9ecef;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                        <div style="display: flex; align-items: center; color: #495057;">
                            <span style="margin-right: 8px;">💰</span>
                            <span style="font-weight: 500;">Unlock Cost</span>
                        </div>
                        <span style="font-weight: 700; color: #212529; font-size: 1.1rem;">${featureData.price_per_unit} <span style="font-size: 0.8em; font-weight: 400; color: #6c757d;">Units</span></span>
                    </div>
                    
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                        <div style="display: flex; align-items: center; color: #495057;">
                            <span style="margin-right: 8px;">🕒</span>
                            <span style="font-weight: 500;">Access Duration</span>
                        </div>
                        <span style="font-weight: 600; color: #212529;">12 Hours</span>
                    </div>
                    
                    <div style="margin-top: 15px; padding-top: 12px; border-top: 2px dashed #dee2e6; display: flex; justify-content: space-between; align-items: center;">
                        <div style="display: flex; align-items: center; color: #495057;">
                            <span style="margin-right: 8px;">💳</span>
                            <span style="font-weight: 500;">Wallet Balance</span>
                        </div>
                        <span style="font-weight: 700; color: #28a745;">${featureData.wallet_balance}</span>
                    </div>
                </div>
                <p style="margin-top: 15px; color: #868e96; font-size: 0.85rem; line-height: 1.4; text-align: center;">
                    Confirming will deduct the units and reveal contact details instantly.
                </p>
            </div>
          `,
          confirmButtonText: 'Unlock & Call',
          showCancelButton: true,
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#6c757d',
          reverseButtons: true,
          didOpen: () => {
            const confirmBtn = Swal.getConfirmButton();
            const cancelBtn = Swal.getCancelButton();
            confirmBtn.style.width = '160px';
            cancelBtn.style.width = '160px';
            confirmBtn.style.padding = '12px 0';
            cancelBtn.style.padding = '12px 0';
          }
        });
        if (!result.isConfirmed) return;
      }

      // Perform unlock
      const unlockRes = await axios.post(`${BaseUrl}/api/agency/contact-unlock`, {
        agency_id: currentUser.id,
        candidate_user_id: userId
      });

      if (unlockRes.data.unlocked || unlockRes.data.already_unlocked) {
        setContactUnlocked(true);
        setUnlockExpiry(unlockRes.data.expires_at);
        openCandidateCall();
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: unlockRes.data.error || 'Failed to unlock contact.' });
      }
    } catch (err) {
      console.error('Error in handleContactClick:', err);
      const errMsg = err.response?.data?.error || 'Something went wrong. Please try again.';
      Swal.fire({ icon: 'error', title: 'Error', text: errMsg, confirmButtonColor: '#d33' });
    }
  };

  // Helper: open native call dialog
  const openCandidateCall = () => {
    const candidatePhone = profileData.whatsapp_number || profileData.phone_number || '9820867896';
    const cleanNumber = candidatePhone.replace(/\D/g, '');
    let finalNumber = cleanNumber;
    if (!cleanNumber.startsWith('+')) {
      if (cleanNumber.startsWith('91') && cleanNumber.length === 12) {
        finalNumber = '+' + cleanNumber;
      } else if (cleanNumber.length === 10) {
        finalNumber = '+91' + cleanNumber;
      } else {
        finalNumber = '+919820867896';
      }
    }
    window.location.href = `tel:${finalNumber}`;
  };

  // Handle view click for related candidates
  const handleViewClick = (userId, first_name) => {
    navigate(`/agencyviewcandidates/${userId}/${first_name}`);
  };

  // Handle message click for related candidates
  const handleMessageClick = (candidate) => {
    Swal.fire({
      title: 'Message Candidate',
      text: `This would open a message modal for ${candidate.first_name}`,
      icon: 'info',
      confirmButtonText: 'OK'
    });
  };

  const handleShortlistClick = async (candidate) => {
    if (!currentUser) {
      Swal.fire({
        title: 'Login Required',
        text: 'Please login as an agency to shortlist candidates',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      navigate('/basicinfo');
      return;
    }

    try {
      const payload = {
        agency_id: currentUser.id,
        candidate_id: candidate.user_id || candidate.id,
        status: 'Interested'
      };

      const response = await fetch(`${BaseUrl}/api/agency/shortlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Shortlisted!',
          text: `${candidate.first_name} has been added to your shortlist.`,
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        const errorData = await response.json();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorData.error || 'Failed to shortlist candidate'
        });
      }
    } catch (err) {
      console.error('Error shortlisting candidate:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while shortlisting'
      });
    }
  };

  const toggleReportForm = () => {
    if (alreadyReported) return;
    setShowReportForm(!showReportForm);
    if (!showReportForm) {
      setReportData({
        email: currentUser?.email || '',
        reason: '',
        description: ''
      });
    }
  };

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      Swal.fire({
        title: 'Login Required',
        text: 'You need to login to report this candidate',
        icon: 'info',
        confirmButtonText: 'Login'
      }).then(() => {
        navigate('/basicinfo');
      });
      return;
    }

    try {
      const payload = {
        emp_id: currentUser.id,
        first_name: currentUser.first_name || 'Anonymous',
        candidate_id: parseInt(userId),
        candidate_name: first_name,
        email: reportData.email,
        reason: reportData.reason,
        description: reportData.description
      };

      const response = await fetch(`${BaseUrl}/api/candidatereport`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Report Submitted',
          text: 'Thank you for reporting this candidate. Our team will review it shortly.',
          confirmButtonColor: '#28a745'
        });
        setShowReportForm(false);
        setAlreadyReported(true);
        setReportData({
          email: currentUser?.email || '',
          reason: '',
          description: ''
        });
      } else {
        const data = await response.json();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.error || 'Failed to submit report',
          confirmButtonColor: '#d33'
        });
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while submitting your report',
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Handle contact unlock
  const handleUnlockContact = async () => {
    if (!currentUser) {
      await Swal.fire({ icon: 'warning', title: 'Login Required', text: 'Please login as an agency to view contact details' });
      navigate('/basicinfo');
      return;
    }

    if (contactUnlocked) return; // Already unlocked

    try {
      // Check subscription info first
      const featureRes = await axios.get(`${BaseUrl}/api/agency/check-feature`, {
        params: { agencyId: currentUser.id, feature: 'contact_unlock', category: 'contact' }
      });
      const featureData = featureRes.data;

      if (!featureData.allowed) {
        if (featureData.reason === 'no_subscription') {
          Swal.fire({
            icon: 'warning', title: 'No Subscription',
            text: 'You need an active Candidate Contact subscription to view contact details.',
            confirmButtonText: 'View Plans', showCancelButton: true
          }).then(r => { if (r.isConfirmed) navigate('/a-subscriptionplans'); });
        } else if (featureData.reason === 'limit_reached') {
          Swal.fire({ icon: 'error', title: 'Limit Reached', text: `You have used all ${featureData.limit} contact unlocks for your plan.` });
        } else if (featureData.reason === 'insufficient_wallet') {
          Swal.fire({
            icon: 'error', title: 'Insufficient Balance',
            text: `Your contact wallet balance (${featureData.balance}) is less than the unlock cost (${featureData.required}). Please top up.`,
            confirmButtonText: 'Top Up', showCancelButton: true
          }).then(r => { if (r.isConfirmed) navigate('/a-subscriptionplans'); });
        } else {
          Swal.fire({ icon: 'warning', title: 'Subscription Required', text: 'Please subscribe to a Candidate Contact plan.' });
        }
        return;
      }

      // For PPC, show confirmation with cost
      if (featureData.plan_type === 'pay_per_click') {
        const result = await Swal.fire({
          title: 'Unlock Contact Details?',
          html: `
            <div style="text-align: left; background: #fff; border-radius: 12px; padding: 5px; font-family: 'Inter', sans-serif;">
                <div style="background: #f8f9fa; border-radius: 10px; padding: 15px; border: 1px solid #e9ecef;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                        <div style="display: flex; align-items: center; color: #495057;">
                            <span style="margin-right: 8px;">💰</span>
                            <span style="font-weight: 500;">Unlock Cost</span>
                        </div>
                        <span style="font-weight: 700; color: #212529; font-size: 1.1rem;">${featureData.price_per_unit} <span style="font-size: 0.8em; font-weight: 400; color: #6c757d;">Units</span></span>
                    </div>
                    
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                        <div style="display: flex; align-items: center; color: #495057;">
                            <span style="margin-right: 8px;">🕒</span>
                            <span style="font-weight: 500;">Access Duration</span>
                        </div>
                        <span style="font-weight: 600; color: #212529;">12 Hours</span>
                    </div>
                    
                    <div style="margin-top: 15px; padding-top: 12px; border-top: 2px dashed #dee2e6; display: flex; justify-content: space-between; align-items: center;">
                        <div style="display: flex; align-items: center; color: #495057;">
                            <span style="margin-right: 8px;">💳</span>
                            <span style="font-weight: 500;">Wallet Balance</span>
                        </div>
                        <span style="font-weight: 700; color: #28a745;">${featureData.wallet_balance}</span>
                    </div>
                </div>
                <p style="margin-top: 15px; color: #868e96; font-size: 0.85rem; line-height: 1.4; text-align: center;">
                    Confirming will deduct the units and reveal contact details instantly.
                </p>
            </div>
          `,
          confirmButtonText: 'Unlock Now',
          showCancelButton: true,
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#6c757d',
          reverseButtons: true,
          didOpen: () => {
            const confirmBtn = Swal.getConfirmButton();
            const cancelBtn = Swal.getCancelButton();
            confirmBtn.style.width = '160px';
            cancelBtn.style.width = '160px';
            confirmBtn.style.padding = '12px 0';
            cancelBtn.style.padding = '12px 0';
          }
        });
        if (!result.isConfirmed) return;
      }

      // Perform unlock
      const unlockRes = await axios.post(`${BaseUrl}/api/agency/contact-unlock`, {
        agency_id: currentUser.id,
        candidate_user_id: userId
      });

      if (unlockRes.data.unlocked || unlockRes.data.already_unlocked) {
        setContactUnlocked(true);
        setUnlockExpiry(unlockRes.data.expires_at);
        Swal.fire({
          icon: 'success',
          title: 'Contact Unlocked!',
          text: 'You now have access to this candidate\'s contact details for 12 hours.',
          timer: 2500,
          showConfirmButton: false
        });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: unlockRes.data.error || 'Failed to unlock contact.' });
      }
    } catch (err) {
      console.error('handleUnlockContact error:', err);
      const errMsg = err.response?.data?.error || 'Something went wrong. Please try again.';
      Swal.fire({ icon: 'error', title: 'Error', text: errMsg });
    }
  };

  // Handle contact details click
  const handleContactDetailsClick = (contactType) => {
    if (contactUnlocked) return; // Already unlocked, do nothing
    handleUnlockContact();
  };

  // Handle modal continue button click
  const handleContinueToSubscription = () => {
    setShowUpgradeModal(false);
    navigate('/a-subscriptionplans');
  };

  // Parse JSON fields safely
  const jobPositions = safeParseArray(profileData?.job_position);
  const languages = safeParseArray(profileData?.language);
  const mainSkills = safeParseArray(profileData?.main_skills);
  const cookingSkills = safeParseArray(profileData?.cooking_skills);
  const otherSkills = safeParseArray(profileData?.other_skills);
  const personality = safeParseArray(profileData?.personality);
  const experience = safeParseArray(profileData?.experience);
  const preferredLocations = safeParseArray(profileData?.preffered_job_locations);
  const education = safeParseArray(profileData?.education);
  const softwareItProgrammingLanguages = safeParseArray(profileData?.software_it_programming_languages);
  const softwareItFrameworks = safeParseArray(profileData?.software_it_frameworks);
  const softwareItDatabaseSkills = safeParseArray(profileData?.software_it_database_skills);
  const softwareItOtherSkills = safeParseArray(profileData?.software_it_other_skills);
  const softwareItCertifications = safeParseArray(profileData?.software_it_certifications);
  const softwareItOtherProgrammingLanguage = String(profileData?.software_it_other_programming_language || '').trim();
  const softwareItOtherCertification = String(profileData?.software_it_other_certification || '').trim();
  const hasSoftwareItDetails =
    softwareItProgrammingLanguages.length > 0 ||
    softwareItFrameworks.length > 0 ||
    softwareItDatabaseSkills.length > 0 ||
    softwareItOtherSkills.length > 0 ||
    softwareItCertifications.length > 0 ||
    Boolean(softwareItOtherProgrammingLanguage) ||
    Boolean(softwareItOtherCertification);

  const formatSoftwareItDisplay = (items = [], otherValue = '') => {
    const normalizedItems = Array.isArray(items) ? items.filter(Boolean) : [];
    const filteredItems = normalizedItems.filter(item => item !== 'Other');
    return otherValue ? [...filteredItems, `Other: ${otherValue}`] : filteredItems;
  };

  // Helper function to format salary
  const formatSalary = (salary) => {
    if (!salary) return '';

    // Convert to number if it's a string
    const salaryNum = typeof salary === 'string' ? parseFloat(salary) : salary;

    if (isNaN(salaryNum)) return salary;

    // Apply floor/ceil logic
    const roundedSalary = salaryNum % 1 >= 0.50 ? Math.ceil(salaryNum) : Math.floor(salaryNum);

    // Remove double zero by converting to integer if it's a whole number
    return roundedSalary % 1 === 0 ? roundedSalary.toString() : roundedSalary.toFixed(2);
  };


  // Render Cook specific fields
  const renderCookSpecificFields = () => (
    <>
      {/* Cooking Skills */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
          <FaUtensils size={20} className="me-2" />
          <span className="ms-2">Cooking Skills</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {cookingSkills.map((item, index) => (
            <span key={index} className="skill-tag">{item}</span>
          ))}
        </div>
      </div>

      {/* Other Skills */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
          <FaStar size={20} className="me-2" />
          <span className="ms-2">Other Skills</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {otherSkills.map((item, index) => (
            <span key={index} className="skill-tag">{item}</span>
          ))}
        </div>
      </div>
    </>
  );

  // Render Teacher specific fields
  const renderTeacherSpecificFields = () => (
    <>
      {/* Teaching Subjects */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
          <FaGraduationCap size={20} className="me-2" />
          <span className="ms-2">Teaching Subjects</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {teachingSubjects.map((subject, index) => (
            <span key={index} className="skill-tag">{subject}</span>
          ))}
        </div>
      </div>

      {/* Teaching Levels */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
          <FaUser size={20} className="me-2" />
          <span className="ms-2">Teaching Levels</span>
        </h6>
        <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {teachingAgeGroups.length > 0 && (
            <div>
              <strong>Age Groups:</strong>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {teachingAgeGroups.map((age, index) => (
                  <span key={index} className="skill-tag">{age}</span>
                ))}
              </div>
            </div>
          )}
          {teachingClasses.length > 0 && (
            <div>
              <strong>Classes:</strong>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {teachingClasses.map((cls, index) => (
                  <span key={index} className="skill-tag">{cls}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Render Nurse specific fields
  const renderNurseSpecificFields = () => (
    <>
      {/* Nursing Qualifications */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
          <FaStethoscope size={20} className="me-2" />
          <span className="ms-2">Nursing Qualifications</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {nursingQualifications.map((qual, index) => (
            <span key={index} className="skill-tag">{qual}</span>
          ))}
        </div>
      </div>

      {/* Nursing Positions */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
          <FaUserNurse size={20} className="me-2" />
          <span className="ms-2">Nursing Positions</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {nursingPositions.map((position, index) => (
            <span key={index} className="skill-tag">{position}</span>
          ))}
        </div>
      </div>

      {/* Prometric Certificate Details */}
      {(profileData.has_prometric_certificate === 1 || profileData.has_prometric_certificate === true) && (
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
            <FaCheckCircle size={20} className="me-2" />
            <span className="ms-2">Prometric Certificate</span>
          </h6>
          <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            <div className="bg-light p-3 rounded">
              <ul className="mb-0 ps-3">
                {profileData.prometric_issued_country && (
                  <li><strong>Issued Country:</strong> {profileData.prometric_issued_country}</li>
                )}
                {profileData.prometric_issue_date && (
                  <li><strong>Issue Date:</strong> {new Date(profileData.prometric_issue_date).toLocaleDateString()}</li>
                )}
                {profileData.prometric_expiry_date && (
                  <li><strong>Expiry Date:</strong> {new Date(profileData.prometric_expiry_date).toLocaleDateString()}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Dataflow Certificate Details */}
      {(profileData.has_dataflow_certificate === 1 || profileData.has_dataflow_certificate === true) && (
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
            <FaCheckCircle size={20} className="me-2" />
            <span className="ms-2">Dataflow Certificate</span>
          </h6>
          <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            <div className="bg-light p-3 rounded">
              <ul className="mb-0 ps-3">
                {profileData.dataflow_issued_country && (
                  <li><strong>Issued Country:</strong> {profileData.dataflow_issued_country}</li>
                )}
                {profileData.dataflow_issue_date && (
                  <li><strong>Issue Date:</strong> {new Date(profileData.dataflow_issue_date).toLocaleDateString()}</li>
                )}
                {profileData.dataflow_expiry_date && (
                  <li><strong>Expiry Date:</strong> {new Date(profileData.dataflow_expiry_date).toLocaleDateString()}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Nursing License Countries */}
      {nursingLicenseCountries.length > 0 && (
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
            <FaIdCard size={20} className="me-2" />
            <span className="ms-2">Nursing License Countries</span>
          </h6>
          <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            {nursingLicenseCountries.map((license, index) => (
              <span key={index} className="skill-tag">{license}</span>
            ))}
          </div>
        </div>
      )}
    </>
  );

  // Render Driver specific fields
  const renderDriverSpecificFields = () => {
    const countries = drivingLicenseCountries || [];
    const filtered = countries.filter(c => c && c !== "I don't have driving license" && c !== "I Don't have License" && c !== "Not Licensed");

    return (
      <>
        {/* Driving License */}
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
            <FaCar size={20} className="me-2" />
            <span className="ms-2">Driving License</span>
          </h6>
          <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            <div>
              <strong>Countries:</strong>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {filtered.length > 0 ? filtered.map((country, index) => (
                  <span key={index} className="skill-tag">{country}</span>
                )) : (
                  <span className="skill-tag">Not Licensed</span>
                )}
              </div>
            </div>
            {carTypesDriven.length > 0 && (
              <div>
                <strong>Car Types:</strong>
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {carTypesDriven.map((type, index) => (
                    <span key={index} className="skill-tag">{type}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <>
        <AgencyNavbar />
        <div className="text-center mt-5" style={{ marginTop: '50px' }}>Loading profile data...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AgencyNavbar />
        <div className="text-center mt-5 text-danger">Error: {error}</div>
      </>
    );
  }

  if (!profileData) {
    return (
      <>
        <AgencyNavbar />
        <div className="text-center mt-5">No profile data found</div>
      </>
    );
  }

  return (
    <>
      <AgencyNavbar />

      {/* Upgrade to Gold Modal */}
      <Modal
        show={showUpgradeModal}
        onHide={() => setShowUpgradeModal(false)}
        centered
        size="sm"
        dialogClassName="small-upgrade-modal"
      >
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="text-warning w-100 text-center" style={{ fontSize: '1.1rem' }}>
            Upgrade to Gold Plan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          <div className="text-center">
            <p className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
              To access contact details
            </p>
            <p className="mb-0" style={{ fontSize: '0.9rem' }}>
              Please upgrade to the Gold Plan
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button
            variant="secondary"
            onClick={() => setShowUpgradeModal(false)}
            size="sm"
            className="px-3"
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleContinueToSubscription}
            size="sm"
            className="px-3"
          >
            Upgrade to Gold
          </Button>
        </Modal.Footer>
      </Modal>

      <Container className="p-3 candidate-view-container">
        <div className="back-button-container">
          <Button variant="outline-secondary" onClick={handleBack}>
            ← Back to Candidates List
          </Button>
        </div>

        {isDoctorProfile ? (
          <div className="dr-profile-container">
            <Container className="p-0 candidate-view-container">
              <Row className="mx-0">
                <Col md={8} className="px-3 mb-4">
                  <div className="combined-profile-details-card bg-white shadow rounded-4">
                    {/* Doctor Profile Header */}
                    <div className="dr-profile-background">
                      <div className="d-flex justify-content-between dr-profile-newbutton">
                        {/* <div className="can-new-badge">New</div> */}
                        {/* <div className="can-contact">Contact</div> */}
                      </div>

                      <div className="d-flex justify-content-center position-relative mt-3">
                        <div className="profile-img-wrapper position-relative mx-auto mb-3">
                          {profileData.profile_photo ? (
                            <img
                              src={profileData.profile_photo}
                              alt="Profile"
                              className="profile-img rounded-circle"
                              style={{ width: '150px', height: '150px', objectFit: 'cover', marginLeft: '25px' }}
                            />
                          ) : (
                            <div
                              className="profile-img rounded-circle d-flex align-items-center justify-content-center bg-light overflow-hidden"
                              style={{ width: '150px', height: '150px' }}
                            >
                              <Image
                                src={defaultProfileImage}
                                className="w-100 h-100"
                                alt="Default profile"
                                style={{ objectFit: 'cover' }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 text-center">
                        <p className="fw-bold text-primary mb-1 candidate-name-font-size">
                          Name - Dr. {profileData.first_name} | Age - {profileData.age} years
                        </p>
                        <p className="text-dark small fw-semibold">
                          Posted {new Date(profileData.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <hr className="profile-divider" />

                    {/* Personal Information Section - Updated styling */}
                    <div className="dr-profile-card p-3">
                      <div className="dr-profile-header">
                        <FaIdCard size={32} className="dr-profile-icon" />
                        <span className="dr-profile-title">Personal Information</span>
                      </div>

                      <div className="dr-profile-card-body">
                        <h5 className="dr-profile-sub-title">
                          <FaCheckCircle className="dr-profile-check-icon" /> Doctor Information
                        </h5>

                        <div className="dr-profile-info">
                          <div className="info-row">
                            <span className="dot"></span> Position - {profileData.dtr_medical_job_position || "Doctor"}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Qualification - {profileData.dtr_ug_qualification} | {profileData.dtr_pg_qualification}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Language - {profileData.dtr_english_lang_qualifications}, {profileData.dtr_other_language_qualifications}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Country License - {profileData.dtr_country_doctor_license?.map(license => license.country_name).join(", ")}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Religion - {profileData.religion}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Nationality - {profileData.nationality}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Current Location - {profileData.current_country}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Gender - {profileData.gender}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Marital status - {profileData.marital_status}
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Post Internship Clinical exp. - {profileData.dtr_internship_clinical_experience} Yrs
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Post Graduation Clinical exp. - {profileData.dtr_graduation_clinical_experience} Yrs
                          </div>
                          <div className="info-row">
                            <span className="dot"></span> Total year of exp. as a Doctor - {profileData.dtr_years_of_experience_doctor} Yrs
                          </div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Current
                          Hospital Name, Starting & Ending Date
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_currently_working_hospital_name}</div>
                          <div className="dr-profile-box">{profileData.dtr_hospital_country}</div>
                        </div>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{new Date(profileData.dtr_work_start_date).toLocaleDateString()}</div>
                          <div className="dr-profile-box">
                            {profileData.dtr_work_end_date === "...On going"
                              ? "...On going"
                              : profileData.dtr_work_end_date
                                ? new Date(profileData.dtr_work_end_date).toLocaleDateString()
                                : "Running"
                            }
                          </div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Previous
                          Hospital Name , Starting & Ending Date
                        </h5>
                        {profileData.dtr_previous_hospital_details?.map((hospital, index) => (
                          <div key={index}>
                            <div className="dr-hospital-row">
                              <div className="dr-profile-box">{hospital.previous_hospital_name}</div>
                              <div className="dr-profile-box">{hospital.hospital_country_name}</div>
                            </div>
                            <div className="dr-hospital-row">
                              <div className="dr-profile-box">{new Date(hospital.work_start_date).toLocaleDateString()}</div>
                              <div className="dr-profile-box">{new Date(hospital.work_end_date).toLocaleDateString()}</div>
                            </div>
                          </div>
                        ))}

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Fellowship
                          Qualifications
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_fellowship_qualifications}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Other
                          Language Qualifications
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_other_language_qualifications}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Language
                          Proficiency Levels
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_language_proficiency}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" />Cultural Adaptation Qualifications
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_cultural_qualifications}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Prometric Certificate
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_prometric_certificate ? "Yes" : "No"}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" />Prometric Certificate Issuing Country
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_prometric_issued_country}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" />Data flow certificate
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_data_flow_certificate ? "Yes" : "No"}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Data flow certificate Issuing Country
                        </h5>
                        <div className="dr-hospital-row">
                          <div className="dr-profile-box">{profileData.dtr_data_flow_issued_by_country}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> My Preferred country for a job
                        </h5>
                        <div className="dr-hospital-row">
                          {profileData.dtr_preferred_country?.map((country, index) => (
                            <div key={index} className="dr-profile-box">{country}</div>
                          ))}
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Language Skilled
                        </h5>
                        <div className="dr-hospital-row">
                          {profileData.dtr_language_skilled?.map((language, index) => (
                            <div key={index} className="dr-profile-box">{language}</div>
                          ))}
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> I have Doctor License From
                        </h5>
                        <div className="dr-hospital-row">
                          {profileData.dtr_country_doctor_license?.map((license, index) => (
                            <div key={index} className="dr-profile-box-license">{license.country_name}</div>
                          ))}
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" />I have Driving License From
                        </h5>
                        <div className="dr-hospital-row">
                          {(() => {
                            const countries = profileData.dtr_driving_license_country || [];
                            const filtered = countries.filter(c => c && c !== "I don't have driving license" && c !== "I Don't have License" && c !== "Not Licensed");
                            return filtered.length > 0 ? filtered.map((country, index) => (
                              <div key={index} className="dr-profile-box">{country}</div>
                            )) : (
                              <div className="dr-profile-box">Not Licensed</div>
                            );
                          })()}
                        </div>

                        {/* Professional Information Header - Updated styling */}
                        <div className="dr-profile-header-professional mt-4">
                          <span className="dr-profile-sub-title">Professional Information</span>
                        </div>

                        <div className="dr-profile-body">
                          <h5 className="dr-profile-sub-title mt-4">
                            <FaCheckCircle className="dr-profile-check-icon" />My Medical Specialties Job Position
                          </h5>
                          <div className="dr-hospital-row">
                            <div className="dr-profile-box">{profileData.dtr_medical_job_position}</div>
                          </div>

                          <h5 className="dr-profile-sub-title mt-4">
                            <FaCheckCircle className="dr-profile-check-icon" />My Surgical Specialties Job Position
                          </h5>
                          <div className="dr-hospital-row">
                            <div className="dr-profile-box">{profileData.dtr_surgical_job_position}</div>
                          </div>

                          <h5 className="dr-profile-sub-title mt-4">
                            <FaCheckCircle className="dr-profile-check-icon" />Other Medical Specialties Job Position
                          </h5>
                          <div className="dr-hospital-row">
                            <div className="dr-profile-box">{profileData.dtr_other_medical_job_position}</div>
                          </div>

                          <h5 className="dr-profile-sub-title mt-4">
                            <FaCheckCircle className="dr-profile-check-icon" />My Academic and Research Job Position
                          </h5>
                          <div className="dr-hospital-row">
                            <div className="dr-profile-box">{profileData.dtr_academic_job_position}</div>
                          </div>

                          <h5 className="dr-profile-sub-title mt-4">
                            <FaCheckCircle className="dr-profile-check-icon" />My Administrative and Leadership Job Position
                          </h5>
                          <div className="dr-hospital-row">
                            <div className="dr-profile-box">{profileData.dtr_administrative_leadership_job_position}</div>
                          </div>

                          <h5 className="dr-profile-sub-title mt-4">
                            <FaCheckCircle className="dr-profile-check-icon" />Job Type
                          </h5>
                          <div className="dr-hospital-row">
                            <div className="dr-profile-box">{profileData.job_type}</div>
                          </div>

                          <h5 className="dr-profile-sub-title mt-4">
                            <FaCheckCircle className="dr-profile-check-icon" />Current work status
                          </h5>
                          <div className="dr-hospital-row">
                            <div className="dr-profile-box">{profileData.current_work_status}</div>
                          </div>

                          <h5 className="dr-profile-sub-title mt-4">
                            <FaCheckCircle className="dr-profile-check-icon" />Visa Type
                          </h5>
                          <div className="dr-hospital-row">
                            <div className="dr-profile-box">{profileData.visa_type}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr className="profile-divider" />

                    {/* Job Preference Section - Updated styling */}
                    <div className="dr-profile-card p-3">
                      <div className="dr-profile-header mt-1">
                        <FaBriefcase size={37} className="dr-profile-icon" />
                        <span className="dr-profile-title">Previous experience</span>
                      </div>

                      <h5 className="dr-profile-sub-title mt-4" style={{ marginLeft: '20px' }}>
                        <FaCheckCircle className="dr-profile-check-icon" /> Country have worked before
                      </h5>
                      <div className="dr-hospital-row" style={{ marginLeft: '20px' }}>
                        {profileData.dtr_before_worked_country?.map((country, index) => (
                          <div key={index} className="dr-profile-box">{country}</div>
                        ))}
                      </div>

                      <h5 className="dr-profile-sub-title mt-4" style={{ marginLeft: '20px' }}>
                        <FaCheckCircle className="dr-profile-check-icon" /> Reference letter from your previous employer
                      </h5>
                      <div className="dr-hospital-row ">
                        <div className="dr-profile-box" style={{ marginLeft: '20px' }}>{profileData.reference_letter ? "Yes" : "No"}</div>
                      </div>

                      <h5 className="dr-profile-sub-title mt-4" style={{ marginLeft: '20px' }}>
                        <FaCheckCircle className="dr-profile-check-icon" /> Passport
                      </h5>
                      <div className="dr-hospital-row ">
                        <div className="dr-profile-box" style={{ marginLeft: '20px' }}>
                          {profileData.dtr_valid_passport ? "Yes, I have Passport" : "No"}
                        </div>
                      </div>

                      <div className="dr-profile-header">
                        <FaLaptop size={37} className="dr-profile-icon" />
                        <span className="dr-profile-title">My preference for job</span>
                      </div>

                      <div className="dr-profile-card-body text-center">
                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" />Job position I am applying for
                        </h5>
                        <div className="dr-hospital-row ">
                          <div className="dr-profile-box">{profileData.dtr_job_position_for_applying}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> Prefer job Location
                        </h5>
                        <div className="dr-hospital-row ">
                          {profileData.dtr_preferred_job_location?.map((location, index) => (
                            <div key={index} className="dr-profile-box">{location}</div>
                          ))}
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" />I would like to start my job
                        </h5>
                        <div className="dr-hospital-row ">
                          <div className="dr-profile-box">{new Date(profileData.dtr_job_start_date).toLocaleDateString()}</div>
                        </div>

                        <h5 className="dr-profile-sub-title mt-4">
                          <FaCheckCircle className="dr-profile-check-icon" /> My Expectation
                        </h5>

                        <div className="dr-hospital-row ">
                          <div className="dr-profile-box text-center">
                            <div className="dr-profile-item ">
                              <span>
                                Salary - {parseFloat(profileData.dtr_min_monthly_sal).toString().replace(/\.0+$/, "")} -
                                {parseFloat(profileData.dtr_max_monthly_sal).toString().replace(/\.0+$/, "")}{" "}
                                {profileData.dtr_country_currency}
                              </span>
                            </div>
                            <div className="dr-profile-item ">
                              <span className="dr-profile-dot"></span>
                              <span>Day Off - {profileData.dtr_day_off_preference}</span>
                            </div>
                            <div className="dr-profile-item ">
                              <span className="dr-profile-dot"></span>
                              <span>Accommodation - {profileData.dtr_accommodation_preference}</span>
                            </div>
                          </div>
                        </div>

                        {/* Contact Details Section - Updated styling */}
                        <div className="dr-profile-card p-3 mb-4" style={{ marginTop: '20px' }}>
                          <div className="dr-profile-header-contact-heading-dr">
                            <FaPhoneAlt size={32} className="dr-profile-icon" />
                            <span className="dr-profile-title">Contact Details</span>
                          </div>

                          <div className="dr-profile-card-body text-center">
                            <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-contactdetails">
                              {contactUnlocked ? (
                                <div className="d-flex flex-column align-items-center">
                                  {profileData?.source?.toLowerCase() === 'agency' && agencyContactDetails ? (
                                    // Agency candidate contact details for Gold users
                                    <>
                                      <div className="info-row">
                                        <FaBuilding size={28} color="#0078d7" />
                                        <span><strong>Agency Name:</strong> {agencyContactDetails.agency_name || "Not provided"}</span>
                                      </div>

                                      {/* Phone Number Field (First) */}
                                      <div className="info-row">
                                        <FaPhoneAlt size={28} color="#007bff" />
                                        <span><strong>Phone Number:</strong> {agencyContactDetails.official_phone_country_code ? `+${agencyContactDetails.official_phone_country_code} ${agencyContactDetails.official_phone}` : agencyContactDetails.whatsapp_number_1_country_code ? `+${agencyContactDetails.whatsapp_number_1_country_code} ${agencyContactDetails.whatsapp_number_1}` : "Not provided"}</span>
                                      </div>

                                      {agencyContactDetails.official_phone && (
                                        <div className="info-row">
                                          <FaPhoneAlt className="me-2" style={{ color: '#007bff' }} />
                                          <span><strong>Official Phone:</strong> +{agencyContactDetails.official_phone_country_code} {agencyContactDetails.official_phone}</span>
                                        </div>
                                      )}

                                      <div className="info-row">
                                        <FaEnvelope size={28} color="#0078d7" />
                                        <span><strong>Agency Email:</strong> {agencyContactDetails.email || "Not provided"}</span>
                                      </div>

                                      <div className="info-row" style={{ cursor: 'pointer' }}>
                                        <span
                                          onClick={() => navigate(`/agencyprofile/${profileData.agency_uid}`)}
                                          style={{
                                            textDecoration: 'underline',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            color: '#007bff'
                                          }}
                                        >
                                          <FaUser size={28} />
                                          View Agency Profile
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    // Regular candidate contact details for Gold users
                                    <>
                                      <div className="dr-profile-info-contact">
                                        {/* Phone Number Field (First) */}
                                        <div className="info-row">
                                          <FaPhoneAlt size={28} color="#007bff" />
                                          <span><strong>Phone Number:</strong> {profileData.whatsapp_country_code && profileData.whatsapp_number ? `${profileData.whatsapp_country_code} ${profileData.whatsapp_number}` : profileData.phone_number ? profileData.phone_number : "Not provided"}</span>
                                        </div>

                                        {profileData.whatsapp_country_code && profileData.whatsapp_number && (
                                          <div className="info-row">
                                            <FaWhatsapp size={28} color="#25D366" />
                                            <span><strong>WhatsApp:</strong> {profileData.whatsapp_country_code} {profileData.whatsapp_number}</span>
                                          </div>
                                        )}

                                        {profileData.telegram_country_code && profileData.telegram_number && (
                                          <div className="info-row">
                                            <FaTelegramPlane size={28} color="#0088cc" />
                                            <span><strong>Telegram:</strong> {profileData.telegram_country_code} {profileData.telegram_number}</span>
                                          </div>
                                        )}

                                        {profileData.botim_country_code && profileData.botim_number && (
                                          <div className="info-row">
                                            <FaBold size={28} style={{ color: '#007bff' }} />
                                            <span><strong>Botim:</strong> {profileData.botim_country_code} {profileData.botim_number}</span>
                                          </div>
                                        )}

                                        <div className="info-row">
                                          <FaEnvelope size={28} color="#0078d7" />
                                          <span><strong>Email:</strong> {profileData.email_id || "Not provided"}</span>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="d-flex flex-column align-items-center">
                                  {profileData?.source?.toLowerCase() === 'agency' ? (
                                    // Agency candidate - show clickable contact fields for non-Gold plans
                                    <>
                                      <div className="dr-profile-info">
                                        <div className="info-row" onClick={() => handleContactDetailsClick('agency')} style={{ cursor: 'pointer' }}>
                                          <FaBuilding size={28} color="#0078d7" />
                                          <span><strong>Agency Name</strong> </span>
                                        </div>
                                        {/* Phone Number Field (First) */}
                                        <div className="info-row" onClick={() => handleContactDetailsClick('phone')} style={{ cursor: 'pointer' }}>
                                          <FaPhoneAlt size={28} color="#007bff" />
                                          <span><strong>Phone Number</strong></span>
                                        </div>
                                        <div className="info-row" onClick={() => handleContactDetailsClick('whatsapp')} style={{ cursor: 'pointer' }}>
                                          <FaWhatsapp size={28} color="#25D366" />
                                          <span><strong>WhatsApp Numbers</strong> </span>
                                        </div>
                                        <div className="info-row" onClick={() => handleContactDetailsClick('email')} style={{ cursor: 'pointer' }}>
                                          <FaEnvelope size={28} color="#0078d7" />
                                          <span><strong>Agency Email</strong></span>
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    // Regular candidate - show clickable contact fields for non-Gold plans
                                    <>
                                      <div className="dr-profile-info-contact">
                                        {/* Phone Number Field (First) */}
                                        <div className="info-row" onClick={() => handleContactDetailsClick('phone')} style={{ cursor: 'pointer' }}>
                                          <FaPhoneAlt size={28} color="#007bff" />
                                          <span><strong>Phone Number</strong></span>
                                        </div>
                                        <div className="info-row" onClick={() => handleContactDetailsClick('whatsapp')} style={{ cursor: 'pointer' }}>
                                          <FaWhatsapp size={28} color="#25D366" />
                                          <span><strong>WhatsApp</strong></span>
                                        </div>
                                        <div className="info-row" onClick={() => handleContactDetailsClick('telegram')} style={{ cursor: 'pointer' }}>
                                          <FaTelegramPlane size={28} color="#0088cc" />
                                          <span><strong>Telegram</strong></span>
                                        </div>
                                        <div className="info-row" onClick={() => handleContactDetailsClick('botim')} style={{ cursor: 'pointer' }}>
                                          <FaBold size={28} className="" style={{ color: '#007bff' }} />
                                          <span><strong>Botim:</strong></span>
                                        </div>
                                        <div className="info-row" onClick={() => handleContactDetailsClick('email')} style={{ cursor: 'pointer' }}>
                                          <FaEnvelope size={28} color="#0078d7" />
                                          <span><strong>Email</strong></span>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="text-center">
                          <h5 className="dr-profile-title-aboutme mt-4" >About Me</h5>
                          <div className="dr-profile-aboutme-box mx-auto">
                            {profileData.dtr_resume_description || profileData.resume_description || 'No description provided'}
                          </div>
                          <div className="text-end reporting-font-styling" style={{ marginTop: '20px' }}>
                            {alreadyReported ? (
                              <span className="text-primary reporting-font-styling">
                                This candidate already reported
                              </span>
                            ) : (
                              <button
                                className="text-primary text-decoration-none border-0 bg-transparent reporting-font-styling"
                                onClick={toggleReportForm}
                              >
                                Report about this Candidate
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Report Form for Doctor Profile */}
                    {showReportForm && (
                      <div className="report-job-section p-3">
                        <div className="shadow p-4 report-form-container" style={{ width: '60%', margin: '0 auto' }}>
                          <h5 className="mb-3 reporting-font-styling">Report this candidate</h5>
                          <Form onSubmit={handleReportSubmit}>
                            <Form.Group className="mb-3">
                              <Form.Label>Your email address</Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                className="report-job-input"
                                value={reportData.email}
                                onChange={handleReportChange}
                                placeholder="Enter your email"
                                required
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Reason for reporting Candidate</Form.Label>
                              <Form.Select
                                name="reason"
                                className="report-job-select"
                                value={reportData.reason}
                                onChange={handleReportChange}
                                required
                              >
                                <option value="">Please select</option>
                                <option value="Fraudulent">Fraudulent</option>
                                <option value="Misleading">Misleading</option>
                                <option value="Discriminatory">Discriminatory</option>
                                <option value="Other">Other</option>
                              </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Please describe your issues with this candidate</Form.Label>
                              <Form.Control
                                as="textarea"
                                name="description"
                                className="report-job-textarea"
                                value={reportData.description}
                                onChange={handleReportChange}
                                rows={4}
                                required
                              />
                            </Form.Group>

                            <div className="report-job-warning">
                              ⚠️ To help fast track investigation, please include any relevant details that prompted you to report this candidate.
                            </div>

                            <div className="d-flex flex-column my-5">
                              <Button type="submit" className="mb-3 report-job-button reporting-font-styling">
                                Report Candidate
                              </Button>
                              <Button variant="link" className="report-job-cancel" onClick={toggleReportForm}>
                                Cancel
                              </Button>
                            </div>
                          </Form>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Matching Candidates Section */}
                <Col md={4} className="px-3 mb-4 mt-4">
                  <div className="related-candidates-sidebar">
                    <h3 className="mb-4 related-candidates-title text-primary fw-bold">Matching Candidates</h3>
                    {(!user.plan_name || !['Gold', 'Silver'].includes(user.plan_name)) ? (
                      <div className="locked-matching-candidates-container position-relative">
                        <div className="blur-overlay d-flex flex-column align-items-center justify-content-center text-center p-4 rounded-4" style={{
                          position: 'absolute',
                          top: 0, left: 0, right: 0, bottom: 0,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          backdropFilter: 'blur(8px)',
                          zIndex: 10,
                          border: '1px solid #ddd'
                        }}>
                          <h5 className="fw-bold mb-3">Priority Matching Locked</h5>
                          <p className="small mb-4 text-muted">To access matching candidates, upgrade your plan to Gold or Silver.</p>
                          <Button 
                            variant="primary" 
                            onClick={() => window.location.href = '/subscription'}
                            className="rounded-pill px-4 fw-bold"
                          >
                            Upgrade Now
                          </Button>
                        </div>
                        {/* Mock Blurred Content */}
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="mb-3 opacity-25">
                             <div className="card p-3 shadow-sm border-0 rounded-4">
                                <div className="d-flex align-items-center gap-3">
                                   <div className="rounded-circle bg-light" style={{width: '60px', height: '60px'}}></div>
                                   <div className="flex-grow-1">
                                      <div className="bg-light h-25 w-75 mb-2"></div>
                                      <div className="bg-light h-25 w-50"></div>
                                   </div>
                                </div>
                             </div>
                          </div>
                        ))}
                      </div>
                    ) : relatedCandidates.length === 0 ? (
                      <div className="text-center no-jobs-message">No matching candidates found</div>
                    ) : (
                      <div className="related-candidates-container">
                        {relatedCandidates.map((candidate) => (
                          <div key={candidate.user_id} className="related-candidate-card">
                            <CandidateCard
                              candidate={candidate}
                              onViewClick={() =>
                                handleViewClick(candidate.user_id, candidate.first_name)
                              }
                              onMessageClick={() => handleMessageClick(candidate)}
                              user={user}
                              navigate={navigate}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        ) : (
          // Regular Candidate Profile UI (unchanged)
          <Row>
            <Col md={8} className="mb-4">
              <div className="combined-profile-details-card bg-white shadow rounded-4 p-3">
                {/* Profile Section */}
                <div className="profile-section p-3">
                  <div className='bg-light'>
                    <div className="d-flex justify-content-between px-2 mb-2 profile-button-container">
                      {/* <span className="profile-badge-candidate-employer">New</span> */}
                      {/* <span className="profile-badge-candidate-employer">Contact</span> */}
                    </div>
                    <div className="profile-img-wrapper position-relative mx-auto mb-3">
                      {profileData.profile_photo ? (
                        <img
                          src={profileData.profile_photo}
                          alt="Profile"
                          className="profile-img rounded-circle"
                          style={{ width: '150px', height: '150px', objectFit: 'cover', marginLeft: "25px" }}
                        />
                      ) : (
                        <div
                          className="profile-img rounded-circle d-flex align-items-center justify-content-center bg-light overflow-hidden"
                          style={{ width: '150px', height: '150px' }}
                        >
                          <Image
                            src={defaultProfileImage}
                            className="w-100 h-100"
                            alt="Default profile"
                            style={{ objectFit: 'cover' }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="d-flex justify-content-between px-3 mb-2">
                      <span className="profile-label-green">Gulf Exp.</span>
                      <span className="profile-label-green">{profileData.accommodation_preference}</span>
                    </div>
                  </div>
                  <div className="profile-info bg-light p-3 rounded-3">
                    <h5 className="fw-bold mb-2 candidate-name-font-size">
                      Name- {profileData.first_name.split('@')[0]}, {profileData.age} Years Old
                    </h5>
                    <p className="mb-0 fw-bold">
                      Job Position - <span className="fw-bold">{jobPositions.join(', ')}</span>
                    </p>

                    {/* Nurse Specific Certifications - Quick Summary at Top */}
                    {isNurse() && (
                      <div className="nurse-top-certifications mt-1">
                        {(profileData.has_prometric_certificate === 1 || profileData.has_prometric_certificate === true) && (
                          <span className="badge bg-success me-2 mb-1">
                            <FaCheckCircle className="me-1" /> Prometric Certified
                          </span>
                        )}
                        {(profileData.has_dataflow_certificate === 1 || profileData.has_dataflow_certificate === true) && (
                          <span className="badge bg-info me-2 mb-1">
                            <FaCheckCircle className="me-1" /> Dataflow Verified
                          </span>
                        )}
                        {nursingLicenseCountries.length > 0 && (
                          <span className="badge bg-warning text-dark mb-1">
                            <FaIdCard className="me-1" /> Licensed Nurse
                          </span>
                        )}
                      </div>
                    )}

                    <p className="mb-0">Total Experience - {profileData.total_work_experience === 'Fresher (0 Year)' || profileData.total_work_experience === '0' || profileData.total_work_experience === 0 ? 'Fresher' : profileData.total_work_experience}</p>
                    <p className="mb-1">Current Location - {profileData.current_country}</p>

                  </div>
                </div>

                <hr className="profile-divider" />

                <div className="cook-details-section p-3">
                  <div className=" desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaUser size={20} className="me-2" />
                      <span className="ms-2">Personal Information</span>
                    </h6>
                    <div className="cook-info-box candidate-view-content-wrapper-emp candidate-view-skill-info">
                      <ul className="list-group list-group-flush candidate-view-skill-info">
                        <li className="list-group-item cook-info-item">
                          <span className="text-success">•</span> Gender - {profileData.gender}
                        </li>
                        <li className="list-group-item cook-info-item">
                          <span className="text-success">•</span> Religion - {profileData.religion}
                        </li>
                        <li className="list-group-item cook-info-item">
                          <span className="text-success">•</span> Marital Status - {profileData.marital_status}
                        </li>
                        <li className="list-group-item cook-info-item">
                          <span className="text-success">•</span> Nationality - {profileData.nationality}
                        </li>
                        <li className="list-group-item cook-info-item">
                          <span className="text-success">•</span> Qualification - {education[0]?.level || 'Not specified'}
                        </li>
                        <li className="list-group-item cook-info-item">
                          <span className="text-success">•</span> Language - {languages.map(lang => lang.language).join(', ')}
                        </li>
                        <li className="list-group-item cook-info-item">
                          <span className="text-success">•</span> Experience - {profileData.total_work_experience === 'Fresher (0 Year)' || profileData.total_work_experience === '0' || profileData.total_work_experience === 0 ? 'Fresher' : profileData.total_work_experience}
                        </li>
                      </ul>
                    </div>
                  </div>

                  {languages.map((lang, idx) => (
                    <div className="mb-3 desktop-section candidate-view-desktop-center" key={idx}>
                      <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                        <FaLanguage size={20} className="me-2" />
                        <span className="ms-2">{lang.language} Language</span>
                      </h6>
                      <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                        {lang.speaking_level && <span className="language-tag">Speak: {lang.speaking_level}</span>}
                        {lang.can_read && <span className="language-tag">Read</span>}
                        {lang.can_write && <span className="language-tag">Write</span>}
                      </div>
                    </div>
                  ))}

                  <div className="mb-3 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaCogs size={20} className="me-2" />
                      <span className="ms-2">Main Skills</span>
                    </h6>
                    <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      {mainSkills.map((skill, idx) => (
                        <span key={idx} className="skill-tag">{skill}</span>
                      ))}
                    </div>
                  </div>

                  {hasSoftwareItDetails && (
                    <div className="mb-3 desktop-section candidate-view-desktop-center">
                      <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                        <FaLaptop size={20} className="me-2" />
                        <span className="ms-2">Software / IT Details</span>
                      </h6>
                      <div className="cook-info-box candidate-view-content-wrapper-emp candidate-view-skill-info">
                        <ul className="list-group list-group-flush candidate-view-skill-info">
                          {formatSoftwareItDisplay(softwareItProgrammingLanguages, softwareItOtherProgrammingLanguage).length > 0 && (
                            <li className="list-group-item cook-info-item">
                              <span className="text-success">•</span> Programming Languages: {formatSoftwareItDisplay(softwareItProgrammingLanguages, softwareItOtherProgrammingLanguage).join(', ')}
                            </li>
                          )}
                          {softwareItFrameworks.length > 0 && (
                            <li className="list-group-item cook-info-item">
                              <span className="text-success">•</span> Frameworks / Technologies: {softwareItFrameworks.join(', ')}
                            </li>
                          )}
                          {softwareItDatabaseSkills.length > 0 && (
                            <li className="list-group-item cook-info-item">
                              <span className="text-success">•</span> Database Skills: {softwareItDatabaseSkills.join(', ')}
                            </li>
                          )}
                          {softwareItOtherSkills.length > 0 && (
                            <li className="list-group-item cook-info-item">
                              <span className="text-success">•</span> Other Skills: {softwareItOtherSkills.join(', ')}
                            </li>
                          )}
                          {formatSoftwareItDisplay(softwareItCertifications, softwareItOtherCertification).length > 0 && (
                            <li className="list-group-item cook-info-item">
                              <span className="text-success">•</span> Certifications: {formatSoftwareItDisplay(softwareItCertifications, softwareItOtherCertification).join(', ')}
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Job Position Specific Fields */}
                  {isCook() && renderCookSpecificFields()}
                  {isTeacher() && renderTeacherSpecificFields()}
                  {isNurse() && renderNurseSpecificFields()}
                  {isDriver() && renderDriverSpecificFields()}

                  <div className="mb-3 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaUserFriends size={20} className="me-2" />
                      <span className="ms-2">Personality</span>
                    </h6>
                    <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      {personality.map((trait, index) => (
                        <span key={index} className="skill-tag">{trait}</span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-3 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaBriefcase size={20} className="me-2" />
                      <span className="ms-2">Work Experience</span>
                    </h6>

                    <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
                      {experience.length > 0 ? (
                        experience.map((exp, expIdx) => {
                          const countryDisplay = Array.isArray(exp.country)
                            ? exp.country.join(', ')
                            : exp.country || '';
                          const jobPositionDisplay = Array.isArray(exp.job_position)
                            ? exp.job_position.join(', ')
                            : exp.job_position || '';
                          const dutiesDisplay = Array.isArray(exp.house_duties)
                            ? exp.house_duties.join(', ')
                            : exp.house_duties;

                          return (
                            <div className="bg-light p-3 rounded candidate-view-experience-item" key={expIdx}>
                              <ul className="mb-2 ps-3">
                                <li>{exp.join_month} {exp.join_year} - {exp.exit_month} {exp.exit_year}</li>
                                <li>{jobPositionDisplay} {countryDisplay}</li>
                                <li>{exp.employer_nationality} Family</li>
                                <li>Duties - {dutiesDisplay}</li>
                              </ul>
                            </div>
                          );
                        })
                      ) : (
                        <div className="bg-light p-3 rounded mt-3 candidate-view-experience-item">
                          <p>No work experience listed</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-4 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaHome size={20} className="me-2" />
                      <span className="ms-2">Accommodation</span>
                    </h6>

                    <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      {(Array.isArray(profileData.accommodation_preference)
                        ? profileData.accommodation_preference
                        : profileData.accommodation_preference
                          ?.split(',')
                          .filter(Boolean)
                      )?.map((item, index) => (
                        <span key={index} className="skill-tag">
                          {item.trim()}
                        </span>
                      ))}
                    </div>
                  </div>



                  <div className="mb-4 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaCalendarAlt size={20} className="me-2" />
                      <span className="ms-2">Day Off</span>
                    </h6>

                    <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      {(Array.isArray(profileData.day_off_preference)
                        ? profileData.day_off_preference
                        : profileData.day_off_preference
                          ?.split(',')
                          .filter(Boolean)
                      )?.map((item, index) => (
                        <span key={index} className="skill-tag">
                          {item.trim()}
                        </span>
                      ))}
                    </div>
                  </div>



                  <div className="mb-4 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaExchangeAlt size={20} className="me-2" />
                      <span className="ms-2">Reason to change the Job</span>
                    </h6>

                    <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      {(Array.isArray(profileData.current_work_status)
                        ? profileData.current_work_status
                        : profileData.current_work_status
                          ?.split(',')
                          .filter(Boolean)
                      )?.map((item, index) => (
                        <span key={index} className="skill-tag">
                          {item.trim()}
                        </span>
                      ))}
                    </div>
                  </div>


                  <div className="mb-4 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaMapMarkerAlt size={20} className="me-2" />
                      <span className="ms-2">Prefer Location</span>
                    </h6>

                    <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      {(Array.isArray(preferredLocations)
                        ? preferredLocations
                        : preferredLocations?.split(',').filter(Boolean)
                      )?.map((loc, index) => (
                        <span key={index} className="skill-tag">
                          {loc.trim()}
                        </span>
                      ))}

                      {profileData.other_job_location && (
                        <span className="skill-tag">
                          {profileData.other_job_location}
                        </span>
                      )}
                    </div>
                  </div>



                  <div className="mb-4 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaFileAlt size={20} className="me-2" />
                      <span className="ms-2">NOC letter / Transfer letter</span>
                    </h6>

                    <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      <span className="skill-tag">
                        {profileData.noc_or_transfer_letter ? 'Yes, I have' : 'No'}
                      </span>
                    </div>
                  </div>

                  {/* </div> */}

                  {/* <hr className="profile-divider" /> */}

                  {/* Profile Details Section */}
                  {/* <div className="profile-details-section p-3"> */}
                  <div className="mb-4 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaPassport size={20} className="me-2" />
                      <span className="ms-2">Visa Status</span>
                    </h6>

                    <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      <span className="skill-tag">
                        {profileData.visa_type}
                      </span>
                    </div>
                  </div>



                  <div className="mb-4 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaMoneyBillAlt size={20} className="me-2" />
                      <span className="ms-2">My Salary Expectation</span>
                    </h6>

                    <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                      <span className="skill-tag">
                        Qatar – {formatSalary(profileData.expected_monthly_salary_qar)} QR
                      </span>

                      <span className="skill-tag">
                        USD – ${formatSalary(profileData.expected_monthly_salary_usd)}
                      </span>
                    </div>
                  </div>


                  {/* Contact Details Section - Updated styling */}
                  <div className="dr-profile-card p-3 mb-4">
                    <div className="dr-profile-header-contact-heading">
                      <FaPhoneAlt size={32} className="dr-profile-icon" />
                      <span className="dr-profile-title">Contact Details</span>
                    </div>

                    <div className="dr-profile-card-body text-center">
                      <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-contactdetails">
                        {contactUnlocked ? (
                          <div className="d-flex flex-column align-items-center">
                            {profileData?.source?.toLowerCase() === 'agency' && agencyContactDetails ? (
                              // Agency candidate contact details for Gold users
                              <>
                                <div className="dr-profile-info">
                                  <div className="info-row">
                                    <FaBuilding size={28} color="#0078d7" />
                                    <span><strong>Agency Name:</strong> {agencyContactDetails.agency_name || "Not provided"}</span>
                                  </div>

                                  {/* Phone Number Field (First) */}
                                  <div className="info-row">
                                    <FaPhoneAlt size={28} color="#007bff" />
                                    <span><strong>Phone Number:</strong> {agencyContactDetails.official_phone_country_code ? `+${agencyContactDetails.official_phone_country_code} ${agencyContactDetails.official_phone}` : agencyContactDetails.whatsapp_number_1_country_code ? `+${agencyContactDetails.whatsapp_number_1_country_code} ${agencyContactDetails.whatsapp_number_1}` : "Not provided"}</span>
                                  </div>

                                  {agencyContactDetails.official_phone && (
                                    <div className="info-row">
                                      <FaPhoneAlt size={28} style={{ color: '#007bff' }} />
                                      <span><strong>Official Phone:</strong> +{agencyContactDetails.official_phone_country_code} {agencyContactDetails.official_phone}</span>
                                    </div>
                                  )}

                                  <div className="info-row">
                                    <FaEnvelope size={28} color="#0078d7" />
                                    <span><strong>Agency Email:</strong> {agencyContactDetails.email || "Not provided"}</span>
                                  </div>
                                </div>

                                <div className="info-row" style={{ cursor: 'pointer', marginTop: "5px", marginLeft: "-5px" }}>
                                  <span
                                    onClick={() => navigate(`/agencyprofile/${profileData.agency_uid}`)}
                                    style={{
                                      textDecoration: 'underline',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      color: '#007bff'
                                    }}
                                  >
                                    <div style={{ display: "flex", gap: "10px" }}>
                                      <FaUser size={28} />
                                      View Agency Profile
                                    </div>
                                  </span>
                                </div>
                              </>
                            ) : (
                              // Regular candidate contact details for Gold users
                              <>
                                <div className="dr-profile-info-contact-nrml">
                                  {/* Phone Number Field (First) */}
                                  <div className="info-row">
                                    <FaPhoneAlt size={28} color="#007bff" />
                                    <span><strong>Phone Number:</strong> {profileData.whatsapp_country_code && profileData.whatsapp_number ? `${profileData.whatsapp_country_code} ${profileData.whatsapp_number}` : profileData.phone_number ? profileData.phone_number : "Not provided"}</span>
                                  </div>

                                  {profileData.whatsapp_country_code && profileData.whatsapp_number && (
                                    <div className="info-row">
                                      <FaWhatsapp size={28} color="#25D366" />
                                      <span><strong>WhatsApp:</strong> {profileData.whatsapp_country_code} {profileData.whatsapp_number}</span>
                                    </div>
                                  )}

                                  {profileData.telegram_country_code && profileData.telegram_number && (
                                    <div className="info-row">
                                      <FaTelegramPlane size={28} color="#0088cc" />
                                      <span><strong>Telegram:</strong> {profileData.telegram_country_code} {profileData.telegram_number}</span>
                                    </div>
                                  )}

                                  {profileData.botim_country_code && profileData.botim_number && (
                                    <div className="info-row">
                                      <FaBold size={28} style={{ color: '#007bff' }} />
                                      <span><strong>Botim:</strong> {profileData.botim_country_code} {profileData.botim_number}</span>
                                    </div>
                                  )}

                                  <div className="info-row">
                                    <FaEnvelope size={28} color="#0078d7" />
                                    <span><strong>Email:</strong> {profileData.email_id || "Not provided"}</span>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            {profileData?.source?.toLowerCase() === 'agency' ? (
                              // Agency candidate - show clickable contact fields for non-Gold plans
                              <>
                                <div className="dr-profile-info">
                                  <div className="info-row" onClick={() => handleContactDetailsClick('agency')} style={{ cursor: 'pointer' }}>
                                    <FaBuilding size={28} color="#0078d7" />
                                    <span><strong>Agency Name</strong></span>
                                  </div>
                                  {/* Phone Number Field (First) */}
                                  <div className="info-row" onClick={() => handleContactDetailsClick('phone')} style={{ cursor: 'pointer' }}>
                                    <FaPhoneAlt size={28} color="#007bff" />
                                    <span><strong>Phone Number</strong></span>
                                  </div>
                                  <div className="info-row" onClick={() => handleContactDetailsClick('whatsapp')} style={{ cursor: 'pointer' }}>
                                    <FaWhatsapp size={28} color="#25D366" />
                                    <span><strong>WhatsApp Numbers</strong></span>
                                  </div>
                                  <div className="info-row" onClick={() => handleContactDetailsClick('email')} style={{ cursor: 'pointer' }}>
                                    <FaEnvelope size={28} color="#0078d7" />
                                    <span><strong>Agency Email</strong></span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              // Regular candidate - show clickable contact fields for non-Gold plans
                              <>
                                <div className="dr-profile-info-contact-nrml">
                                  {/* Phone Number Field (First) */}
                                  <div className="info-row" onClick={() => handleContactDetailsClick('phone')} style={{ cursor: 'pointer' }}>
                                    <FaPhoneAlt size={28} color="#007bff" />
                                    <span><strong>Phone Number</strong></span>
                                  </div>
                                  <div className="info-row" onClick={() => handleContactDetailsClick('whatsapp')} style={{ cursor: 'pointer' }}>
                                    <FaWhatsapp size={28} color="#25D366" />
                                    <span><strong>WhatsApp</strong></span>
                                  </div>
                                  <div className="info-row" onClick={() => handleContactDetailsClick('telegram')} style={{ cursor: 'pointer' }}>
                                    <FaTelegramPlane size={28} color="#0088cc" />
                                    <span><strong>Telegram</strong></span>
                                  </div>
                                  <div className="info-row" onClick={() => handleContactDetailsClick('botim')} style={{ cursor: 'pointer' }}>
                                    <FaBold size={28} className="" style={{ color: '#007bff' }} />
                                    <span><strong>Botim</strong></span>
                                  </div>
                                  <div className="info-row" onClick={() => handleContactDetailsClick('email')} style={{ cursor: 'pointer' }}>
                                    <FaEnvelope size={28} color="#0078d7" />
                                    <span><strong>Email</strong></span>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 desktop-section candidate-view-desktop-center">
                    <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                      <FaInfoCircle size={20} className="me-2" />
                      <span className="ms-2">About Me</span>
                    </h6>
                    <div className="employer-candidate-viewdetails-aboutme candidateview-view-about-box">
                      <div className="about-me-box p-2 border rounded candidate-view-about-box" style={{ textAlign: "justify" }}>
                        {profileData.resume_description}
                      </div>
                    </div>
                  </div>

                  {profileData.upload_video && (
                    <div className="mb-3 desktop-section candidate-view-desktop-center">
                      <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                        <FaGraduationCap size={20} className="me-2" />
                        <span className="ms-2">YouTube Video Link</span>
                      </h6>
                      <div className="candidate-view-content-wrapper-emp">
                        <div className="badge-pill-container-youtube">
                          <a href={profileData.upload_video} target="_blank" rel="noopener noreferrer">
                            Click here to watch introduction video
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-end reporting-font-styling">
                    {alreadyReported ? (
                      <span className="text-primary reporting-font-styling" >
                        This candidate already reported
                      </span>
                    ) : (
                      <button
                        className="text-primary text-decoration-none border-0 bg-transparent reporting-font-styling"
                        onClick={toggleReportForm}
                      >
                        Report about this Candidate
                      </button>
                    )}
                  </div>
                </div>

                {showReportForm && (
                  <div className="report-job-section p-3">
                    <div className="shadow p-4 report-form-container" style={{ width: '60%', margin: '0 auto' }}>
                      <h5 className="mb-3 reporting-font-styling">Report this Candidate</h5>
                      <Form onSubmit={handleReportSubmit}>
                        <Form.Group className="mb-3">
                          <Form.Label>Your email address</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            className="report-job-input"
                            value={reportData.email}
                            onChange={handleReportChange}
                            placeholder="Enter your email"
                            required
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Reason for reporting Candidate</Form.Label>
                          <Form.Select
                            name="reason"
                            className="report-job-select"
                            value={reportData.reason}
                            onChange={handleReportChange}
                            required
                          >
                            <option value="">Please select</option>
                            <option value="Fraudulent">Fraudulent</option>
                            <option value="Misleading">Misleading</option>
                            <option value="Discriminatory">Discriminatory</option>
                            <option value="Other">Other</option>
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Please describe your issues with this candidate</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="description"
                            className="report-job-textarea"
                            value={reportData.description}
                            onChange={handleReportChange}
                            rows={4}
                            required
                          />
                        </Form.Group>

                        <div className="report-job-warning">
                          ⚠️ To help fast track investigation, please include any relevant details that prompted you to report this candidate.
                        </div>

                        <div className="d-flex flex-column my-5">
                          <Button type="submit" className="mb-3 report-job-button reporting-font-styling">
                            Report Candidate
                          </Button>
                          <Button variant="link" className="report-job-cancel" onClick={toggleReportForm}>
                            Cancel
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </div>
                )}
              </div>
            </Col>

            <Col md={4} className="mb-4 mt-4">
              <h3 className="mb-4 related-candidates-title">Related Candidates</h3>
              {relatedCandidates.length === 0 ? (
                <div className="text-center no-jobs-message">No related job positions</div>
              ) : (
                <div className="related-candidates-container">
                  {relatedCandidates.map((candidate) => (
                    <div key={candidate.user_id} className="related-candidate-card">
                      <CandidateCard
                        candidate={candidate}
                        onViewClick={() =>
                          handleViewClick(candidate.user_id, candidate.first_name)
                        }
                        onMessageClick={() => handleMessageClick(candidate)}
                        user={user}
                        navigate={navigate}
                        onShortlistClick={handleShortlistClick}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

function CandidateCard({ candidate, onViewClick, onMessageClick, onShortlistClick, user, navigate }) {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getMainLanguage = () => {
    if (!candidate.language || candidate.language.length === 0) return 'Not specified';
    return `${candidate.language[0].language}`;
  };

  const getMainSkills = () => {
    if (!candidate.main_skills || candidate.main_skills.length === 0) return 'Not specified';
    const firstSkill = candidate.main_skills[0];
    if (firstSkill.includes('["')) {
      try {
        const parsed = JSON.parse(firstSkill.replace(/\\/g, ''));
        if (Array.isArray(parsed)) {
          return parsed.join(', ');
        }
        return parsed;
      } catch (e) {
        return firstSkill;
      }
    }
    return firstSkill;
  };

  return (
    <Card
      className="h-100 shadow-sm rounded card-body-alt-candidate mb-4 cursor-pointer"
      onClick={onViewClick}
      style={{ cursor: 'pointer' }}
    >
      <Card.Body className="d-flex flex-column justify-content-center">
        <div className="d-flex">
          <div className="me-3 text-center" style={{ marginTop: '15px' }}>
            <div className="d-flex justify-content-center gap-2 mt-2">
              {Number(candidate.purchased_badge) === 1 && (
                <div className="custom-label-alt verified-label" style={{ width: "60%" }}>
                  <span className="verified-label-icon">&#10003;</span>
                  <span>Verified</span>
                </div>
              )}
              {candidate.verification_status?.toLowerCase() !== "verified" &&
                candidate.created_at &&
                moment().diff(moment(candidate.created_at), "days") <= 7 && (
                  <div className="custom-label-alt" style={{ width: "60%" }}>
                    New
                  </div>
                )}
            </div>
            <div className="profile-img-container-alt">
              {hasVisibleOfferBadge(candidate) && (
                <div className="offer-badge-alt">{getVisibleOfferBadgeText(candidate)}</div>
              )}
              {candidate.profile_photo ? (
                <Image
                  src={candidate.profile_photo}
                  alt="profile"
                  roundedCircle
                  className="border profile-photo-alt"
                />
              ) : (
                <div className="border profile-photo-alt d-flex align-items-center justify-content-center bg-light rounded-circle overflow-hidden">
                  <Image
                    src={defaultProfileImage}
                    roundedCircle
                    className="w-100 h-100"
                    alt="Default profile"
                    style={{ objectFit: 'cover' }}
                  />
                </div>
              )}
            </div>
            <div className="very-active-label-alt">{getVisibleSourceLabel(candidate)}</div>
          </div>
          <div className="flex-grow-1 allcandidate-card-right-alt">
            <h6 className="fw-bold">
              {candidate.first_name.split('@')[0]} - {candidate.age} years -{' '}
              <span className="nationality-text-alt">{candidate.nationality}</span>
            </h6>
            <ul className="small mb-0 custom-list related-candidates-candidateview">
              <li>
                <strong>Job Position - </strong>
                {Array.isArray(candidate.job_position)
                  ? candidate.job_position.join(', ').trim() || 'Not specified'
                  : (candidate.job_position || 'Not specified')}
              </li>
              <li>
                <strong>{candidate.religion || 'Not specified'} - </strong>
                {candidate.marital_status || 'Not specified'}
              </li>
              <li>
                <strong>Salary -</strong>{' '}
                {candidate.expected_monthly_salary_usd || '0'} USD /{' '}
                {candidate.expected_monthly_salary_qar || '0'} QAR
              </li>
              <li>
                <strong>Experience -</strong> {candidate.total_work_experience || 'Not specified'}
              </li>
              <li>
                <strong>Language -</strong> {getMainLanguage()}
              </li>
              <li>
                <strong>Start Date -</strong> {formatDate(candidate.preferred_start_date)}
              </li>
              <li className="current-location-text-alt">
                <strong>Current Location -</strong>{' '}
                <span>{candidate.current_country || 'Not specified'}</span>
              </li>
            </ul>

            {/* Added Video Call Action Buttons for Matching Candidates */}
            <div className="d-flex mt-3 gap-2 align-items-center flex-wrap">
               <Button
                variant="light"
                size="sm"
                className="rounded-pill p-2 d-flex align-items-center justify-content-center shadow-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  const user = JSON.parse(localStorage.getItem("user")) || {};
                  if (!user || (user.role !== 'agency' && user.role !== 'agency_admin')) {
                    import('sweetalert2').then(Swal => {
                        Swal.default.fire('Access Denied', 'Video Call feature is only available to Agencies.', 'error');
                    });
                  } else if (user.plan_name === 'Free' || user.plan_name === 'Free Plan (Contact)') {
                    import('sweetalert2').then(Swal => {
                        Swal.default.fire({
                            icon: 'error',
                            title: 'Upgrade Required',
                            text: 'Video Call feature is available for exclusive plans. Please upgrade.',
                            confirmButtonText: 'Upgrade Now'
                        }).then((result) => {
                            if (result.isConfirmed) window.location.href = '/subscription';
                        });
                    });
                  } else {
                    window.dispatchEvent(new CustomEvent('initiate-video-call', {
                        detail: {
                            candidateId: candidate?.user_id || candidate?.id,
                            candidateName: candidate?.first_name
                        }
                    }));
                  }
                }}
               >
                 <FaVideo className="text-primary" />
               </Button>

               <Button
                variant="outline-primary"
                size="sm"
                className="rounded-pill px-3 py-1 ms-auto fw-bold"
                style={{ fontSize: '10px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewClick();
                }}
               >
                 View Profile
               </Button>
            </div>

            <div className="mt-2 text-center text-muted small fw-medium border-top pt-2 d-flex justify-content-around">
                <div
                    className="cursor-pointer no-underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onShortlistClick) onShortlistClick(candidate);
                    }}
                >
                    <FaRegHeart className="me-1" /> Like
                </div>
                <div
                    className="cursor-pointer no-underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        Swal.fire('Share', 'Share functionality coming soon', 'info');
                    }}
                >
                    <i className="bi bi-share me-1"></i> Share
                </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
export default AgencyViewCandidates;
